(function () {
  const CONTAINER_SELECTOR = '[data-cart-drawer-recommendations]';
  const LIST_SELECTOR = '[data-cart-drawer-recommendations-list]';
  const CARD_SELECTOR = '[data-recommendation-product-id]';
  const MAX_API_LIMIT = 10;

  const toUniqueIds = (value) => {
    return Array.from(
      new Set(
        String(value || '')
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean)
      )
    );
  };

  const getRootUrl = () => {
    return window.Theme?.routes?.root_url || window.Shopify?.routes?.root || '/';
  };

  const buildRecommendationsUrl = (productId, apiLimit, sectionId) => {
    const baseUrl = new URL(getRootUrl(), window.location.origin);
    const url = new URL('recommendations/products', baseUrl);

    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(apiLimit));
    url.searchParams.set('intent', 'related');
    url.searchParams.set('section_id', sectionId);

    return url.toString();
  };

  const fetchRecommendationCards = async (productId, apiLimit, sectionId) => {
    const response = await fetch(buildRecommendationsUrl(productId, apiLimit, sectionId), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Recommendations request failed with status ${response.status}`);
    }

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    return Array.from(parsed.querySelectorAll(CARD_SELECTOR)).map((card) => ({
      id: card.getAttribute('data-recommendation-product-id') || '',
      html: card.outerHTML,
    }));
  };

  const hydrateRecommendations = async (container) => {
    if (!(container instanceof HTMLElement)) return;
    if (container.dataset.recommendationsState === 'loading') return;

    const list = container.querySelector(LIST_SELECTOR);
    if (!(list instanceof HTMLElement)) return;

    const productIds = toUniqueIds(container.dataset.productIds);
    const limit = Math.max(parseInt(container.dataset.limit || '0', 10) || 0, 1);
    const sectionId = container.dataset.sectionId || 'cart-drawer-recommendations';
    const fallbackMarkup = list.innerHTML.trim();

    if (productIds.length === 0) {
      container.hidden = fallbackMarkup === '';
      return;
    }

    container.dataset.recommendationsState = 'loading';

    const seenIds = new Set(productIds);
    const collectedMarkup = [];
    const apiLimit = Math.min(MAX_API_LIMIT, Math.max(limit * 2, limit + productIds.length));

    try {
      for (const productId of productIds) {
        const cards = await fetchRecommendationCards(productId, apiLimit, sectionId);

        for (const card of cards) {
          if (!card.id || seenIds.has(card.id)) continue;

          seenIds.add(card.id);
          collectedMarkup.push(card.html);

          if (collectedMarkup.length >= limit) {
            break;
          }
        }

        if (collectedMarkup.length >= limit) {
          break;
        }
      }

      if (collectedMarkup.length > 0) {
        list.innerHTML = collectedMarkup.join('');
        container.hidden = false;
        container.dataset.recommendationsState = 'loaded';
        return;
      }

      container.hidden = fallbackMarkup === '';
      container.dataset.recommendationsState = fallbackMarkup === '' ? 'empty' : 'fallback';
    } catch (error) {
      console.error(error);
      container.hidden = fallbackMarkup === '';
      container.dataset.recommendationsState = fallbackMarkup === '' ? 'error-empty' : 'fallback';
    }
  };

  const hydrateAllRecommendations = () => {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => {
      void hydrateRecommendations(container);
    });
  };

  const scheduleHydration = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(hydrateAllRecommendations);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateAllRecommendations, { once: true });
  } else {
    hydrateAllRecommendations();
  }

  document.addEventListener('cart:update', scheduleHydration);
  window.addEventListener('pageshow', hydrateAllRecommendations);
})();
